import React, { useEffect, useMemo, useState } from 'react';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import { HbrSpinner } from '@harbor/elements-react-standalone';
import StbModal from '../../security-toolbox-ui/StbModal';
import {
  useDcloudCatalogSearch,
  useDcloudSchedulingOptions,
  useScheduleDcloudSession,
} from '../../../hooks/dcloud-hooks';
import {
  DcloudMetric,
  formatDcloudDateForWire,
  getDcloudDemoId,
  toDateTimeLocalValue,
} from '../../../support/dcloudService';

export type DcloudScheduleTarget = {
  demoId: number | null;
  name?: string;
  /** Used to seed the catalog search when the demo id is not known yet. */
  searchText?: string;
};

type DcloudScheduleSessionModalProps = {
  open: boolean;
  dataCenter: string;
  target?: DcloudScheduleTarget | null;
  onClose: () => void;
  onScheduled?: () => void;
};

const DEFAULT_DURATION_HOURS = 4;

const defaultStart = (): Date => {
  const start = new Date();

  start.setSeconds(0, 0);
  start.setMinutes(start.getMinutes() >= 30 ? 60 : 30);

  return start;
};

const addHours = (date: Date, hours: number) =>
  new Date(date.getTime() + hours * 60 * 60 * 1000);

const DcloudScheduleSessionModal: React.FC<DcloudScheduleSessionModalProps> = ({
  open,
  dataCenter,
  target,
  onClose,
  onScheduled,
}) => {
  const [searchText, setSearchText] = useState('');
  const [demoId, setDemoId] = useState<number | null>(null);
  const [startValue, setStartValue] = useState('');
  const [stopValue, setStopValue] = useState('');
  const [count, setCount] = useState(1);
  const [description, setDescription] = useState('');
  const [justification, setJustification] = useState('');
  const [metricValues, setMetricValues] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);

  const schedule = useScheduleDcloudSession(dataCenter);

  const { data: schedulingOptions, isLoading: loadingOptions } =
    useDcloudSchedulingOptions(dataCenter, { enabled: open });

  const {
    data: catalogResults,
    isFetching: searching,
    isError: searchFailed,
  } = useDcloudCatalogSearch(searchText, dataCenter, { enabled: open });

  // Reset to a clean form each time the modal opens so a previous attempt does
  // not leak into the next one.
  useEffect(() => {
    if (!open) return;

    const start = defaultStart();

    setSearchText(target?.searchText || target?.name || '');
    setDemoId(target?.demoId ?? null);
    setStartValue(toDateTimeLocalValue(start));
    setStopValue(toDateTimeLocalValue(addHours(start, DEFAULT_DURATION_HOURS)));
    setCount(1);
    setDescription('');
    setJustification('');
    setMetricValues({});
    setFormError(null);
    schedule.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, target?.demoId, target?.name, target?.searchText]);

  const demoOptions = useMemo(() => {
    const rows = Array.isArray(catalogResults) ? catalogResults : [];

    const options = rows
      .map((row) => ({
        id: getDcloudDemoId(row),
        name: row?.name || row?.handle || 'Demo',
      }))
      .filter((row) => row.id != null);

    // Keep a pre-selected demo visible even before the search catches up.
    if (demoId != null && !options.some((row) => row.id === demoId)) {
      options.unshift({ id: demoId, name: target?.name || `Demo ${demoId}` });
    }

    return options;
  }, [catalogResults, demoId, target?.name]);

  const mandatoryOptions = useMemo(
    () => (schedulingOptions?.schedulingOptions || []).filter((option) => option.mandatory),
    [schedulingOptions],
  );

  const validate = (): string | null => {
    if (!demoId) return 'Select a demo to schedule.';
    if (!startValue || !stopValue) return 'Provide both a start and an end time.';

    const start = new Date(startValue);
    const stop = new Date(stopValue);

    if (Number.isNaN(start.getTime()) || Number.isNaN(stop.getTime())) {
      return 'Provide a valid start and end time.';
    }

    if (stop <= start) return 'The end time must be after the start time.';

    const missing = mandatoryOptions.find((option) => !metricValues[option.value]);

    if (missing) return `${missing.label} is required by dCloud.`;

    return null;
  };

  const handleSubmit = async () => {
    const validationError = validate();

    setFormError(validationError);

    // The modal is kept open on failure (suppressCloseOnSubmit) so the message
    // stays visible; returning rather than throwing avoids an unhandled
    // rejection out of the footer button.
    if (validationError) return;

    const metrics: DcloudMetric[] = Object.entries(metricValues)
      .filter(([, value]) => String(value || '').trim().length > 0)
      .map(([name, value]) => ({ name, value }));

    try {
      await schedule.mutateAsync({
        demoId: demoId as number,
        start: formatDcloudDateForWire(new Date(startValue)),
        stop: formatDcloudDateForWire(new Date(stopValue)),
        count: count > 1 ? count : undefined,
        ...(description ? { description } : {}),
        ...(justification ? { justification } : {}),
        ...(metrics.length ? { metrics } : {}),
      });
    } catch {
      // Surfaced through schedule.error below.
      return;
    }

    onScheduled?.();
    onClose();
  };

  const submitError = formError || (schedule.isError ? schedule.error?.message : null);

  return (
    <StbModal
      open={open}
      role="save"
      title="Schedule a dCloud session"
      size="large"
      scrollable
      submitLabel="Schedule"
      submitDisabled={!demoId}
      suppressCloseOnSubmit
      onSubmit={handleSubmit}
      onClose={async () => onClose()}
      apiResponseSection={
        submitError ? (
          <p className="m-0 text-sm text-amber-800">{submitError}</p>
        ) : undefined
      }
    >
      <div className="flex flex-col gap-4">
        <p className="m-0 text-sm text-gray-600">
          {`Scheduling in ${dataCenter.toUpperCase()}. Sessions are created in the selected data center only.`}
        </p>

        <TextField
          label="Find a demo"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          placeholder="Search the dCloud catalog"
          helperText={
            searchFailed
              ? 'The dCloud catalog search failed. Try again in a moment.'
              : 'Type at least two characters to search.'
          }
          fullWidth
        />

        <TextField
          select
          label="Demo"
          value={demoId != null ? String(demoId) : ''}
          onChange={(e) => setDemoId(e.target.value ? Number(e.target.value) : null)}
          helperText={
            searching
              ? 'Searching...'
              : demoOptions.length
                ? `${demoOptions.length} match${demoOptions.length === 1 ? '' : 'es'}`
                : 'No matching demos yet.'
          }
          fullWidth
        >
          {demoOptions.map((option) => (
            <MenuItem key={option.id} value={String(option.id)}>
              {option.name}
            </MenuItem>
          ))}
        </TextField>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <TextField
            label="Start"
            type="datetime-local"
            value={startValue}
            onChange={(e) => {
              const next = e.target.value;
              setStartValue(next);

              // Keep the default duration when the start moves.
              const parsed = new Date(next);
              if (!Number.isNaN(parsed.getTime()) && !stopValue) {
                setStopValue(toDateTimeLocalValue(addHours(parsed, DEFAULT_DURATION_HOURS)));
              }
            }}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />

          <TextField
            label="End"
            type="datetime-local"
            value={stopValue}
            onChange={(e) => setStopValue(e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />

          <TextField
            label="Sessions"
            type="number"
            value={count}
            onChange={(e) => setCount(Math.max(1, parseInt(e.target.value, 10) || 1))}
            helperText="Pods to reserve"
            inputProps={{ min: 1 }}
            fullWidth
          />
        </div>

        <TextField
          label="Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="What this session is for"
          fullWidth
        />

        <TextField
          label="Justification"
          value={justification}
          onChange={(e) => setJustification(e.target.value)}
          placeholder="Required by dCloud for some demos"
          multiline
          minRows={2}
          fullWidth
        />

        {loadingOptions ? (
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <HbrSpinner size="small" />
            Loading dCloud scheduling requirements...
          </div>
        ) : (
          (schedulingOptions?.schedulingOptions || []).map((option) => {
            const value = metricValues[option.value] || '';
            const setValue = (next: string) =>
              setMetricValues((current) => ({ ...current, [option.value]: next }));

            if (option.values?.length) {
              return (
                <TextField
                  key={option.value}
                  select
                  label={`${option.label}${option.mandatory ? ' *' : ''}`}
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  fullWidth
                >
                  {option.values.map((choice) => (
                    <MenuItem key={choice.value} value={choice.value}>
                      {choice.label}
                    </MenuItem>
                  ))}
                </TextField>
              );
            }

            return (
              <TextField
                key={option.value}
                label={`${option.label}${option.mandatory ? ' *' : ''}`}
                value={value}
                onChange={(e) => setValue(e.target.value)}
                fullWidth
              />
            );
          })
        )}
      </div>
    </StbModal>
  );
};

export default DcloudScheduleSessionModal;
